import {
  confirmExpenseApproveFlow,
  confirmExpenseDeleteFlow,
  notifyExpenseActionPending,
  openExpenseEditModalFromRow,
  resolveExpenseApprovalTargetState,
  submitExpensePayFlow,
} from '@/composables/expenses/useExpenseFlow';
import {
  fetchExpensesFilterOptions,
  fetchExpensesSnapshot,
  getExpensesFilterOptions,
} from '@/composables/expenses/useExpensesList';
import {
  createDefaultSaleExpensesFilters,
  createInitialExpenseSummary,
} from '@/composables/sales/saleDetail/state';
import { ensureValidPage } from '@/composables/shared/usePagination';
import { EXPENSES_TEXT } from '@/texts/catalog/expenses';

const EXPENSES_FLOW_TEXT = EXPENSES_TEXT.flow;

export const supplierDetailExpenseMethods = {
  resetSupplierExpenseRuntime() {
    this.expenseRows = [];
    this.expenseSummary = createInitialExpenseSummary();
    this.expenseCurrentPage = 1;
    this.expenseActiveStatusTab = 'all';
    this.expenseFilters = createDefaultSaleExpensesFilters();
    this.expenseSearchText = '';
    this.expenseInFlightRequestKey = '';
  },
  ensureExpensePageInRange() {
    this.expenseCurrentPage = ensureValidPage(this.expenseCurrentPage, this.expenseTotalPages);
  },
  clearExpenseSearchDebounce() {
    if (!this.expenseSearchDebounceTimer) {
      return;
    }

    clearTimeout(this.expenseSearchDebounceTimer);
    this.expenseSearchDebounceTimer = null;
  },
  scheduleExpenseSearchFetch() {
    this.clearExpenseSearchDebounce();

    this.expenseSearchDebounceTimer = setTimeout(() => {
      this.expenseSearchDebounceTimer = null;
      this.fetchSupplierExpenses();
    }, this.expenseSearchDebounceMs);
  },
  normalizeSupplierExpensesFilters(filters = {}) {
    const source = (filters && typeof filters === 'object') ? filters : {};

    return {
      search: String(source.search || '').trim(),
      date_start: String(source.date_start || '').trim(),
      date_end: String(source.date_end || '').trim(),
      provider: Array.isArray(source.provider) ? source.provider : [],
      expense_class: Array.isArray(source.expense_class) ? source.expense_class : [],
      category: Array.isArray(source.category) ? source.category : [],
      tags: Array.isArray(source.tags) ? source.tags : [],
      has_xml: Array.isArray(source.has_xml) ? source.has_xml : [],
      currency: Array.isArray(source.currency) ? source.currency : [],
    };
  },
  buildSupplierExpensesParams() {
    const filters = this.normalizeSupplierExpensesFilters(this.expenseFilters);

    return {
      search: String(this.expenseSearchText || filters.search || '').trim(),
      date_start: filters.date_start,
      date_end: filters.date_end,
      provider: [this.supplierId],
      provider_id: this.supplierId,
      supplier_id: this.supplierId,
      expense_class: filters.expense_class,
      category: filters.category,
      tags: filters.tags,
      has_xml: filters.has_xml,
      currency: filters.currency,
    };
  },
  async ensureSupplierExpenseFiltersLoaded() {
    if (this.expenseFiltersLoaded) {
      return;
    }

    try {
      this.expenseFilterOptions = await fetchExpensesFilterOptions();
    } catch (error) {
      this.expenseFilterOptions = getExpensesFilterOptions();
    } finally {
      this.expenseFiltersLoaded = true;
    }
  },
  async fetchSupplierExpenses() {
    if (this.supplierId <= 0) {
      this.resetSupplierExpenseRuntime();
      return;
    }

    await this.ensureSupplierExpenseFiltersLoaded();

    const params = this.buildSupplierExpensesParams();
    const requestKey = JSON.stringify(params);

    if (this.expenseLoading && this.expenseInFlightRequestKey === requestKey) {
      return;
    }

    const requestToken = ++this.expenseListRequestToken;
    this.expenseInFlightRequestKey = requestKey;
    this.expenseLoading = true;

    try {
      const snapshot = await fetchExpensesSnapshot(params);

      if (requestToken === this.expenseListRequestToken) {
        const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
        const payload = snapshot?.metrics && typeof snapshot.metrics === 'object' ? snapshot.metrics : {};

        this.expenseRows = rows;
        this.expenseSummary = {
          total: Number(payload?.total_expenses ?? payload?.total ?? 0) || 0,
          paid: Number(payload?.paid ?? payload?.total_paid ?? 0) || 0,
          pending: Number(payload?.pending_to_pay ?? payload?.pending ?? 0) || 0,
        };
        this.ensureExpensePageInRange();
      }
    } catch (error) {
      if (requestToken === this.expenseListRequestToken) {
        this.expenseRows = [];
        this.expenseSummary = createInitialExpenseSummary();
      }
    } finally {
      if (requestToken === this.expenseListRequestToken) {
        this.expenseLoading = false;
        if (this.expenseInFlightRequestKey === requestKey) {
          this.expenseInFlightRequestKey = '';
        }
      }
    }
  },
  handleSupplierExpensesSearchChange(payload) {
    const nextSearch = String(payload?.search || '').trim();

    this.expenseSearchText = nextSearch;
    this.expenseFilters = {
      ...this.normalizeSupplierExpensesFilters(this.expenseFilters),
      search: nextSearch,
    };
    this.expenseCurrentPage = 1;
    this.scheduleExpenseSearchFetch();
  },
  handleSupplierExpensesFiltersChange(payload) {
    const nextFilters = this.normalizeSupplierExpensesFilters(payload);

    this.expenseFilters = nextFilters;
    this.expenseSearchText = String(nextFilters.search || '').trim();
    this.expenseCurrentPage = 1;
    this.fetchSupplierExpenses();
  },
  handleSupplierExpensesStatusTabSelect(tabKey) {
    const normalized = String(tabKey || '').trim();
    if (normalized === '' || normalized === this.expenseActiveStatusTab) {
      return;
    }

    this.expenseActiveStatusTab = normalized;
    this.expenseCurrentPage = 1;
  },
  handleSupplierExpensesPageChange(nextPage) {
    this.expenseCurrentPage = Number(nextPage || 1);
    this.ensureExpensePageInRange();
  },
  handleSupplierPayExpense(row) {
    if (this.isPayExpenseSubmitting) {
      return;
    }

    this.payExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isPayExpenseModalOpen = true;
  },
  handleSupplierEditExpense(row) {
    openExpenseEditModalFromRow(this, row, {
      mapInitialData: ({ detail, rowData }) => {
        const mergedPayload = {
          ...rowData,
          ...detail,
        };

        if (String(mergedPayload.status || '').trim() === '') {
          mergedPayload.status = String(rowData.status || '').trim();
        }

        if (Number(mergedPayload.payment_id || 0) <= 0) {
          mergedPayload.payment_id = Number(
            detail.payment_id
            || rowData.payment_id
            || rowData.payment?.id
            || 0
          ) || 0;
        }

        if (mergedPayload.is_approved === undefined || mergedPayload.is_approved === null) {
          mergedPayload.is_approved = rowData.is_approved;
        }

        return mergedPayload;
      },
    });
  },
  handleSupplierApproveExpense(row, nextApproved = null) {
    this.approveExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
    this.approveExpenseTargetState = resolveExpenseApprovalTargetState(row, nextApproved);
    this.isApproveExpenseModalOpen = true;
  },
  handleSupplierDeleteExpense(row) {
    this.deleteExpenseTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isDeleteExpenseModalOpen = true;
  },
  handleSupplierDownloadExpenseXml(row) {
    notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadXml });
  },
  handleSupplierDownloadExpensePdf(row) {
    notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadPdf });
  },
  handleSupplierDownloadExpenseReceipt(row) {
    notifyExpenseActionPending(this, row, { actionLabel: EXPENSES_FLOW_TEXT.pending.actions.downloadReceipt });
  },
  closeSupplierApproveExpenseModal() {
    if (this.isApprovingExpense) {
      return;
    }

    this.isApproveExpenseModalOpen = false;
    this.approveExpenseTarget = null;
    this.approveExpenseTargetState = true;
  },
  closeSupplierPayExpenseModal() {
    if (this.isPayExpenseSubmitting) {
      return;
    }

    this.isPayExpenseModalOpen = false;
    this.isPayExpenseSubmitting = false;
    this.payExpenseTarget = null;
  },
  closeSupplierDeleteExpenseModal() {
    if (this.isDeletingExpense) {
      return;
    }

    this.isDeleteExpenseModalOpen = false;
    this.deleteExpenseTarget = null;
  },
  async submitSupplierPayExpense(payload = {}) {
    await submitExpensePayFlow(this, payload, {
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  async confirmSupplierApproveExpense() {
    await confirmExpenseApproveFlow(this, {
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  async confirmSupplierDeleteExpense() {
    await confirmExpenseDeleteFlow(this, {
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  handleSupplierNewExpenseFromOperation() {
    this.openExpenseModal('create', this.buildExpenseCreateInitialData());
  },
};
